import { useToast } from "hooks/index.js";
import React, { useState } from "react";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { HelperText } from "ui/base/helperText/HelperText.jsx";
import {
    convertHexToRGBA,
    defaultThemeColors, mediaSizes,
    themeColor,
} from "ui/theme/index.js";
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp";
import { useUpdateTeacher } from "context/UpdateTeacherContext";
import YouTube from "react-youtube";
import { extractYouTubeVideoId } from "utils/utils.ts";
import {PhotoGalleryUploader} from "ui/base/photoGalleryUploader/PhotoGalleryUploader";
import { LoadingSpinner } from "ui/base/loading/LoadingSpinner";

export const PhotosPage = ({
  onSubmit,
  loading,
  selectedImages,
                               setSelectedImages,
  handleImageDeselect,
}) => {
  const { formData, changeFormData } = useUpdateTeacher();
  const [formDataErrors, setFormDataErrors] = useState({});

  const onClick = () => {
    let submitErrors = {};
    // Validate image count
    const undeletedImages = selectedImages?.filter((img) => !img.deleted);

    if (undeletedImages.length < 2) {
      submitErrors = { ...submitErrors, gallery: "Select at least 2 images" };

      setFormDataErrors((prevState) => ({
        ...prevState,
        gallery: "Select at least 2 images",
      }));
    } else {
      submitErrors = { ...submitErrors, gallery: "" };
      setFormDataErrors((prevState) => ({
        ...prevState,
        gallery: "",
      }));
    }

    // Check for any errors
    const hasErrors = Object.values(submitErrors).some((error) => error !== "");

    if (hasErrors) {
      useToast("error", "Add at least 2 images");
      return;
    }

    onSubmit();
  };
  const youtubeId = extractYouTubeVideoId(formData.intro_video_url);

  return (
    <Container>
      <InnerContainer>
        <HelperText
          style={{
            marginTop: "-1rem",
            marginBottom: "-1rem",
          }}
        >
          Add a video link introducing yourself and telling the users about your
          class.
          <PurpleText>{" (Optional)"}</PurpleText> <br />
          In order to share a video, you would have to upload it to YouTube and
          paste the link below
        </HelperText>
        <InputSignUp
          value={formData.intro_video_url}
          setValue={(value) => {
            changeFormData("intro_video_url", value);
          }}
        />

        {youtubeId ? (
          <VideoWrapper>
            <YouTube
              className="youtube-wrapper desktop"
              iframeClassName="youtube-iframe"
              videoId={extractYouTubeVideoId(formData.intro_video_url)}
            />
          </VideoWrapper>
        ) : null}
        <div>
            <PhotoGalleryUploader
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                handleImageDeselect={handleImageDeselect}
                formDataErrors={formDataErrors}
            />
        </div>

        <div style={{ width: "100%", display: "flex" }}>
          <ButtonPrimary onClick={onClick} disabled={loading}>
            {loading ? (
              <>
                <LoadingSpinner variant="white" />
                <span
                  style={{
                    color: "#d9d9d9",
                  }}
                >
                  Loading...
                </span>
              </>
            ) : (
              "Save Changes"
            )}
          </ButtonPrimary>
        </div>
      </InnerContainer>
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  gap: 1rem;
  padding-bottom: 2rem;
  font-family: var(--font-secondary);
`;

const InnerContainer = styled.div`
  display: grid;
  gap: 40px;
  grid-column: span 2;
`;

const GalleryContainer = styled.div`
  display: grid;
  gap: 1rem;
`;

const GalleryAddButton = styled.label`
  padding: 20px 38px;
  background-color: ${defaultThemeColors.border};
  border-width: 2px;
  border-style: dashed;
  border-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  cursor: pointer;
  height: 140px;
  @media (min-width: 768px) {
    height: 280px;
  }
`;

const GalleryImagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

    @media (min-width: ${mediaSizes.md}) {
        grid-template-columns: repeat(4, 1fr);
    }
    
  @media (min-width: ${mediaSizes.xl}) {
    grid-template-columns: repeat(8, 1fr);
  }
`;

const GalleryAddButtonTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
`;

const GalleryImageContainer = styled.div`
    position: relative;
`;

const MainImage = styled.div`
    background-color: ${defaultThemeColors.pink};
    color: ${defaultThemeColors.white};
    padding-inline: 0.5rem;
    padding-block: 0.625rem;
    border-bottom-right-radius: 1rem;
    border-bottom-left-radius: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
`;

const GalleryImage = styled.img`
    width: 100%;
    height: 90px;
    object-fit: cover;
    object-position: center;
    border-radius: 10px;
    border-width: 1px;
    border-style: solid;
    border-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
`;

const GalleryDeleteImageContainer = styled.div`
  position: absolute;
  background-color: ${defaultThemeColors.pink};
  color: ${defaultThemeColors.white};
  right: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 5px;
`;

const VideoWrapper = styled.div`
  overflow: hidden;
  max-width: 300px;
  margin-inline: auto;

  & > div {
    aspect-ratio: 16/9;
    overflow: hidden;
    border-radius: 5px;

    & .youtube-wrapper,
    .youtube-iframe {
      width: 100%;
      height: 100%;
    }
  }
`;

const PurpleText = styled.p`
  display: inline;
  color: ${themeColor("blue")};
`;
